/**
 * TeamPreview · home-page mini section
 * ---------------------------------------------------------------------------
 * Pulls the owner + first 3 members from `config.team_page` and renders them
 * as premium warrior cards, linking to the full /info/team page.
 *
 * Kept intentionally short — the home page already has a lot to say. The
 * "MEET THE FULL SQUAD" CTA is the money link.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Crown, Shield } from 'lucide-react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const TeamPreview = () => {
  const { config } = useSiteConfig();
  const tp = config.team_page || {};
  const owner = tp.owner || {};
  const members = (tp.members || []).slice(0, 3);
  const cards = [owner, ...members].filter(m => m.name || m.callsign);
  if (!cards.length) return null;

  return (
    <section className="py-16 sm:py-22 relative overflow-hidden" data-testid="team-preview">
      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none opacity-70" style={{
        background: 'radial-gradient(60% 60% at 50% 30%, rgba(255,45,146,.05), transparent 70%), radial-gradient(50% 60% at 20% 80%, rgba(255,211,77,.04), transparent 70%)',
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        <div className="text-center mb-10 sm:mb-14">
          <div className="eh-kicker justify-center mb-3">// THE OPS TEAM</div>
          <h2 className="eh-display font-black leading-tight" style={{ fontSize: 'clamp(1.8rem, 5.5vw, 3.4rem)' }}>
            THE <span className="tp-gold">HANDS</span> BEHIND EVERY <span className="eh-neon">MISSION</span>
          </h2>
          <p className="opacity-70 mt-3 max-w-xl mx-auto text-sm" style={{ fontFamily: 'Inter,sans-serif' }}>
            No bots. No offshore call-centres. Every op is worked by named operators with a decade of scars.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {cards.slice(0, 4).map((m, i) => {
            const isOwner = i === 0 && m === owner;
            const accent = isOwner ? '#ffd34d' : ['#ff2d92', '#4de0ff', '#c084fc', '#00ff9d'][i % 4];
            return (
              <Link
                key={m.id || m.callsign || i}
                to="/info/team"
                data-testid={`team-preview-card-${i}`}
                className="tp-card group relative rounded-2xl overflow-hidden border-2 transition-all"
                style={{
                  borderColor: `${accent}45`,
                  background: `linear-gradient(180deg, ${accent}0d, rgba(0,0,0,.65))`,
                  '--c': accent,
                }}
              >
                <div className="aspect-[3/4] relative overflow-hidden">
                  {m.avatar_url ? (
                    <img
                      src={m.avatar_url}
                      alt={m.name || m.callsign}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center" style={{ background: `${accent}18`, color: accent }}>
                      <Shield size={40} strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="absolute inset-x-0 top-0 h-[3px] opacity-90" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
                  {isOwner && (
                    <span className="absolute top-2.5 left-2.5 eh-mono text-[9px] tracking-widest px-2 py-1 rounded-full flex items-center gap-1 font-bold" style={{ background: accent, color: '#0a0703' }}>
                      <Crown size={9} /> OWNER
                    </span>
                  )}
                  {/* Bottom gradient overlay */}
                  <div className="absolute inset-x-0 bottom-0 h-2/3 pointer-events-none" style={{
                    background: 'linear-gradient(180deg, transparent, rgba(0,0,0,.85) 70%, #000)',
                  }} />
                  <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
                    <div className="eh-mono text-[9.5px] tracking-[0.25em] mb-0.5" style={{ color: accent }}>
                      {m.callsign || `OP-${(i + 1).toString().padStart(2, '0')}`}
                    </div>
                    <div className="font-black leading-tight" style={{ fontFamily: 'Inter,sans-serif', fontSize: 'clamp(0.95rem, 1.5vw, 1.15rem)' }}>
                      {m.name || m.callsign || 'CLASSIFIED'}
                    </div>
                    <div className="text-[10.5px] opacity-70 leading-tight mt-0.5 line-clamp-1">
                      {m.role || m.class || 'Field operator'}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="text-center mt-8 sm:mt-10">
          <Link to="/info/team" className="tp-meet inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 font-bold text-xs tracking-widest transition-all" data-testid="team-preview-cta">
            MEET THE FULL SQUAD <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <style>{`
        .tp-card:hover { transform: translateY(-4px); border-color: var(--c) !important; box-shadow: 0 20px 60px -20px color-mix(in srgb, var(--c) 55%, transparent); }
        .tp-card { transition: transform .4s cubic-bezier(.2,.9,.3,1), border-color .25s, box-shadow .4s; }
        .tp-gold { background: linear-gradient(90deg,#f5d76e,#ffd34d,#f5d76e); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
        .tp-meet {
          background: linear-gradient(90deg, rgba(255,211,77,.15), rgba(0,255,157,.10));
          border-color: rgba(255,211,77,.5);
          color: #ffd34d;
        }
        .tp-meet:hover {
          border-color: #ffd34d;
          background: linear-gradient(90deg, rgba(255,211,77,.28), rgba(0,255,157,.14));
          box-shadow: 0 12px 40px -14px rgba(255,211,77,.4);
        }
      `}</style>
    </section>
  );
};

export default TeamPreview;
